import { Action, createSlice, PayloadAction, ThunkAction } from '@reduxjs/toolkit';
import { SamplingEventFilterData } from 'src/shared/hooks/useProjectFilters';
import { RootState } from 'src/store';

export type SpeciesMapFiltersState = {
    dataSources: string[];
    areas: string[];
    metric: string | null;
    metricLabel: string | null;
    speciesOfInterest: string;
    speciesHighlight: string | null;
    samplingEvent: SamplingEventFilterData | null;
};

const defaultFiltersState = {
    dataSources: [],
    areas: [],
    metric: 'none',
    metricLabel: '',
    speciesOfInterest: 'all',
    speciesHighlight: null,
    samplingEvent: null,
};

export type SpeciesMapUIState = {
    filters: SpeciesMapFiltersState;
    isSidebarOpen: boolean;
    activeSampleId: string | null;
    activeSurveySampleId: string | null;
    hoveredSampleId: string | null;
    hoveredSurveySampleId: string | null;
    focusedSpeciesScientificName: string | null;
    mapType: string;
    showSurveyMarkers: boolean;
};

export const defaultUiState = {
    filters: { ...defaultFiltersState },
    isSidebarOpen: true,
    activeSampleId: null,
    activeSurveySampleId: null,
    hoveredSampleId: null,
    hoveredSurveySampleId: null,
    focusedSpeciesScientificName: null,
    mapType: 'roadmap',
    showSurveyMarkers: false,
};

type SpeciesMapState = {
    uiState: {
        [projectId: string]: SpeciesMapUIState;
    };
};
const initialState: SpeciesMapState = {
    uiState: {},
};

export const updateFilters =
    (fragment: Partial<SpeciesMapFiltersState>): ThunkAction<void, RootState, void, Action<string>> =>
    (dispatch, getState) => {
        const state = getState();

        const projectId = state.projects.projectList.currentProjectId;

        if (projectId) {
            dispatch(
                speciesMapSlice.actions.updateFilters({
                    projectId,
                    fragment,
                })
            );
        }
    };

export const updateUiState =
    (fragment: Partial<SpeciesMapUIState>): ThunkAction<void, RootState, void, Action<string>> =>
    (dispatch, getState) => {
        const state = getState();

        const projectId = state.projects.projectList.currentProjectId;

        if (projectId) {
            dispatch(
                speciesMapSlice.actions.updateUIState({
                    projectId,
                    fragment,
                })
            );
        }
    };

export const resetFilters = (): ThunkAction<void, RootState, void, Action<string>> => (dispatch, getState) => {
    const state = getState();

    const projectId = state.projects.projectList.currentProjectId;

    if (projectId) {
        dispatch(
            speciesMapSlice.actions.resetFilters({
                projectId,
            })
        );
    }
};

const ensureProjectUIStatePresence = (state: SpeciesMapState, projectId: string) => {
    if (!state.uiState[projectId]) {
        state.uiState[projectId] = { ...defaultUiState };
    }
};

const speciesMapSlice = createSlice({
    name: 'speciesMap',
    initialState,
    reducers: {
        updateFilters(state, action: PayloadAction<{ projectId: string; fragment: Partial<SpeciesMapFiltersState> }>) {
            const { projectId, fragment } = action.payload;

            ensureProjectUIStatePresence(state, projectId);

            state.uiState[projectId].filters = {
                ...(state.uiState[projectId].filters as SpeciesMapFiltersState),
                ...fragment,
            };

            state.uiState[projectId].hoveredSampleId = null;
            state.uiState[projectId].activeSampleId = null;
        },

        updateUIState(state, action: PayloadAction<{ projectId: string; fragment: Partial<SpeciesMapUIState> }>) {
            const { projectId, fragment } = action.payload;

            ensureProjectUIStatePresence(state, projectId);

            state.uiState[projectId] = {
                ...(state.uiState[projectId] as SpeciesMapUIState),
                ...fragment,
            };
        },
        resetFilters(state, action: PayloadAction<{ projectId: string }>) {
            const { projectId } = action.payload;
            state.uiState[projectId].filters = { ...defaultFiltersState };
        },
    },
});

export default speciesMapSlice.reducer;
